/*
 * Authors: mariushirjanu
 * Created: 24/06/2022
 */
import { Component, Input, Output, OnInit, EventEmitter } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { FormlyAlertComponent } from '../../../shared/formly/components/formly-alert.component';
import { IDialogButton } from '../../../shared/message/ga-message.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { IOrderFeesConfirmProforma, IOrderFeesHeader, OrderFeesDataService } from './order-fees-data.service';
import { Alert } from '../../../shared/app-styles';
import { util } from '../../../shared/util';

@Component({
  selector: 'confirm-proforma-dialog',
  template: `
    <default-dialog [title]="title" [buttons]="buttons">
      <formly [form]="form" [fields]="fields" [model]="model"></formly>
    </default-dialog>
  `
})
export class ConfirmProformaDialogComponent implements OnInit {
  @Input() public title:string;
  @Input() public dueDate:Date;
  @Input() public invoiceId:number;
  @Output() public onSaved:EventEmitter<IOrderFeesHeader> = new EventEmitter<IOrderFeesHeader>();

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IOrderFeesConfirmProforma;
  public buttons:IDialogButton[];

  constructor(
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private orderFeesDataService:OrderFeesDataService,
    private validateService:GaValidateService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.start();
  }

  private start():void {
    this.buttons = [ {
      label: this.title,
      className: 'btn btn-success',
      onClick: () => this.doConfirm()
    }, {
      label: 'GA.BUTTONS.CANCEL',
      className: 'btn btn-danger'
    } ];

    this.fields = this.createFields();
    this.model = {
      dueDate: this.dueDate
    };
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];
    const labelClass:string = 'col-md-4';
    const valueClass:string = 'col-md-8';

    fields.push(
      this.formlyService.aValue('dueDate')
        .withFormat(this.formattingService.formatDate)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aCustom()
        .withComponent(FormlyAlertComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              alertClass: `${Alert.WARNING} no-margin`,
              message: 'INVOICE.ORDERFEES.EXPECTEDPAYMENTDATE.TEXT'
            }
          }
        })
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .build(),

      this.formlyService.aDateInput('expectedPaymentDate')
        .withLabelClass(labelClass)
        .withValueClass('col-md-3')
        .build(),

      this.formlyService.aTextarea('reason')
        .isRequired()
        .withEditable('expectedPaymentDateReason')
        .withLabelClass('col-md-12')
        .withValueClass('col-md-12')
        .withHideExpression(() => {
          const expectedPaymentDate:number = new Date(this.model.expectedPaymentDate).getTime();
          const dueDate:number = new Date(this.model.dueDate).getTime();
          return !this.model.expectedPaymentDate || (expectedPaymentDate <= dueDate);
        })
        .build(),

      this.formlyService.anInput('poNumber')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass('col-md-5')
        .build(),

      this.formlyService.aCheckbox('confirmed')
        .isRequired()
        .withEditable('checkboxConfirm')
        .withoutWrapper()
        .withValidationMessage('required', 'INVOICE.ORDERFEES.CHECKBOXCONFIRMVALIDATION.TEXT')
        .build()
    );

    return fields;
  }

  private doConfirm():void {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => this.orderFeesDataService.confirmProforma(this.invoiceId, this.model))
      .then((invoiceHeader:IOrderFeesHeader) => {
        this.onSaved.emit(invoiceHeader);
      })
      .catch(util.noop);
  }
}